import { memo, useMemo } from "react";
// import { v4 as uuidv4 } from 'uuid';
import { LinearStatisticDiagram } from "ui/molecules/StatisticDiagram";
import type { LinearDiagramProps } from "../../types";
import { formatXDateAxis, getAxisBottomTickRotation, getLinearLegendItems } from "../../support/helpers";
import ChartLegend from "./ChartLegend";

type ExtendedLinearDiagramProps = LinearDiagramProps & {
    readonly legendFormatValue?: (value: number) => string;
    readonly legendPostfix?: string;
    readonly legendWidth?: number;
};

const LinearDiagram = ({
    isLoading,
    title,
    subtitle,
    subheader,
    data,
    axisLeft,
    chartMargin,
    yFormat,
    sliceTooltip,
    sx,
    renderAxisBottomTick,
    colors,
    className = '',
    columns = [],
    legendFormatValue,
    legendPostfix,
    legendWidth,
    'data-test-id': dataTestId
}: ExtendedLinearDiagramProps) => {
    const legendItems = useMemo(() => {
        if (!data?.length) {
            return [];
        }

        if (typeof colors === 'function') {
            const colorFn = colors as (input: { id: string | number }) => string;
            return getLinearLegendItems({
                series: data,
                colorFor: id => colorFn({ id })
            });
        }

        return getLinearLegendItems({ series: data });
    }, [data, colors]);

    // TODO: this is workaround for re-rendering chart when data is loaded
    // const [key, setKey] = useState('');
    // useEffect(() => {
    //     !isLoading && setKey(uuidv4());
    // }, [isLoading]);

    return (
        <LinearStatisticDiagram
            // key={key}
            sx={{
                position: 'relative',
                overflow: 'visible',
                ...sx
            }}
            className={className}
            isLoading={isLoading}
            isDataAvailable={Boolean(data.length)}
            title={title}
            subtitle={subtitle}
            subheader={subheader}
            data-test-id={dataTestId}
            legendSlot={!isLoading && (
                <ChartLegend
                    items={legendItems}
                    formatValue={legendFormatValue}
                    postfix={legendPostfix}
                    width={legendWidth}
                />
            )}
            chartProps={{
                margin: {
                    top: 50,
                    bottom: 50,
                    left: 60,
                    ...chartMargin
                },
                xScale: {
                    type: 'point'
                },
                yScale: {
                    type: 'linear'
                },
                enableSlices: 'x',
                sliceTooltip,
                yFormat,
                curve: 'monotoneX',
                // enableArea: true,
                axisTop: null,
                axisRight: null,
                axisBottom: {
                    format: formatXDateAxis(columns),
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: getAxisBottomTickRotation(data),
                    legendOffset: 36,
                    legendPosition: 'middle',
                    renderTick: renderAxisBottomTick
                },
                axisLeft: {
                    format: '.2s',
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legendOffset: -45,
                    legendPosition: 'middle',
                    ...axisLeft
                },
                enablePoints: true,
                colors: colors ?? { scheme: 'category10' },
                // pointSize: 10,
                // pointColor: { theme: 'background' },
                // pointBorderWidth: 2,
                // pointBorderColor: { from: 'serieColor' },
                // pointLabelYOffset: -12,
                useMesh: true,
                data
            }}
        />
    );
};

export default memo(LinearDiagram);
